import React, {useMemo} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {cssTheme} from '@vio/atlas/themes'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {getRoundingStrategy, RoundingStrategy} from '@vio/core/src/utils/number'

import {Price, PriceSize} from '../Price'

interface Props {
  /** Whether the price should be styled as an anchor price */
  anchorPrice: number
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Price currency code */
  currencyCode: string
  /** The price should be styled as a private deal */
  isPrivateDeal?: boolean
  /** Whether the price is an app lock deal */
  isAppLockedDeal?: boolean
  /** The value to be displayed as the price */
  price: number
  /** Whether we should show an anchor price */
  showAnchorPrice?: boolean
  /** Whether the locked icon can be shown. It is only shown when isAppLockedDeal is also true. */
  showAppIcon?: boolean
  /** Whether the elements should be displayed in a row layout */
  showRowLayout?: boolean
  /** Whether the element should be displayed inline to its siblings ie; not flex */
  shouldDisplayInline?: boolean
  /** Price Size */
  size?: PriceSize
  /** Shows the indication by the side of the price */
  showIndication?: boolean
  /** Overrides the indication ("/ night" / "total") text color. Defaults to neutral.secondary. */
  indicationColor?: string
  /** Overrides the indication ("/ night" / "total") text size. Defaults to bodyXS. */
  indicationSize?: 'bodyXS' | 'bodyS'
  /** Shows the indication that the price is total by the side of the price */
  showTotal?: boolean

  /** Total number of guests */
  numberOfGuests?: number
  /** Shows the CTAOffer  */
  shouldAlignOffer?: boolean
  /** Rounding strategy to apply to the price */
  roundingStrategy?: RoundingStrategy
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
}

export const PriceWrapper = styled.div`
  &.--inline {
    display: inline-block;
  }

  &.--row {
    display: flex;
    flex-direction: row;
    align-items: baseline;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: ${cssTheme.layout.spacing.s200};
  }

  &.--row-left-aligned {
    justify-content: flex-start;
  }

  &.--column {
    display: flex;
    flex-direction: column;
  }
`

export const MessageWrapper = styled.div<{
  indicationColor?: string
  indicationSize?: 'bodyXS' | 'bodyS'
}>`
  ${({indicationSize}) =>
    indicationSize === 'bodyS'
      ? cssTheme.typography.bodyS
      : cssTheme.typography.bodyXS};
  white-space: nowrap;
  color: ${({indicationColor}) =>
    indicationColor ?? cssTheme.colors.content.neutral.secondary};

  &.--size-sm {
    padding-bottom: ${cssTheme.layout.spacing.s100};
    padding-left: ${cssTheme.layout.spacing.s200};
  }

  &.--size-default {
    padding-bottom: ${cssTheme.layout.spacing.s200};
    padding-left: ${cssTheme.layout.spacing.s200};
  }
`

export const PriceAndMessageWrapper = styled.div`
  display: flex;
  align-items: flex-end;
`

export const GuestMessage = styled.span`
  text-transform: lowercase;
`

export const PriceComposition = ({
  price,
  anchorPrice,
  currencyCode,
  isPrivateDeal = false,
  showRowLayout,
  shouldDisplayInline = false,
  showAnchorPrice = false,
  showAppIcon = false,
  isAppLockedDeal = false,
  size,
  showIndication = false,
  indicationColor,
  indicationSize,
  showTotal = false,
  shouldAlignOffer = false,
  roundingStrategy = getRoundingStrategy(),
  className,
  dataId
}: Props) => {
  const renderPriceIndication = useMemo(() => {
    const sizeVariant = size === 'sm' ? '--size-sm' : '--size-default'
    if (showIndication) {
      // Shows the indication that the price is total by the side of the price
      if (showTotal) {
        return (
          <MessageWrapper
            className={cx(sizeVariant)}
            indicationColor={indicationColor}
            indicationSize={indicationSize}
          >
            <FormattedMessageWrapper
              id="price.totalV2"
              defaultMessage="total"
            />
          </MessageWrapper>
        )
      }

      // Shows the indication that the price is per night by the side of the price
      return (
        <MessageWrapper
          className={cx(sizeVariant)}
          indicationColor={indicationColor}
          indicationSize={indicationSize}
        >
          <FormattedMessageWrapper id="perNight" defaultMessage="/ night" />
        </MessageWrapper>
      )
    }

    return null
  }, [size, showIndication, indicationColor, indicationSize, showTotal])

  return (
    <PriceWrapper
      className={cx(
        className,
        shouldDisplayInline && '--inline',
        `--${showRowLayout ? 'row' : 'column'}`
      )}
    >
      {showAnchorPrice && anchorPrice > 0 && (
        <Price
          dataId={`Anchor-${dataId}`}
          isAnchor
          value={anchorPrice}
          currency={currencyCode}
          roundingStrategy={roundingStrategy}
          shouldAlignOffer={shouldAlignOffer}
        />
      )}
      <PriceAndMessageWrapper>
        <Price
          dataId={dataId}
          value={price}
          currency={currencyCode}
          isPrivate={isPrivateDeal}
          isAppLocked={isAppLockedDeal}
          roundingStrategy={roundingStrategy}
          showAppIcon={showAppIcon}
          size={size}
          shouldAlignOffer={shouldAlignOffer}
        />
        {renderPriceIndication}
      </PriceAndMessageWrapper>
    </PriceWrapper>
  )
}
